import React, { useState } from 'react';
import { Receipt } from 'lucide-react';
import { Button } from './ui/button';
import api from '../lib/api';
import { tableLabel } from '../lib/tableLabel';
import { toast } from 'sonner';

// Local alias — the renderReceiptHtml runs in a child print window so it
// references `tabLabel` via template-string interpolation (above) which
// would otherwise need to be inlined. Keep both in lockstep.
const tabLabel = (n) => tableLabel(n);

/**
 * TabReceiptButton
 * ================
 * Fetches /api/tabs/{tab_id}/receipt and opens a print-ready combined
 * receipt window covering every order on the tab + the split-payment
 * breakdown (if any). Per owner request: a tab generates ONE receipt,
 * not N (one per order).
 */
function fmtMoney(n) { return `$${Number(n || 0).toFixed(2)}`; }

// Escape user-controlled strings before injecting into the print-window
// HTML template — customer_name, item.name, settlement_method etc. flow
// in unsanitised and could otherwise break out of the receipt context
// (e.g. a customer named `</script><img onerror=...>`).
function esc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function renderReceiptHtml(data) {
  const { tab, orders, partials, sum_total: sumTotal } = data;
  const settledAt = tab.closed_at ? new Date(tab.closed_at).toLocaleString('en-AU') : '—';
  const upfront = Number(tab.upfront_amount || tab.paid_amount || 0);
  const discount = Number(tab.discount_amount || 0);
  const finalTotal = Number(tab.final_total ?? (sumTotal - discount));
  const owed = Math.max(0, finalTotal - upfront);
  const methodLabel = esc(tab.settlement_method || 'tab');

  const itemRows = orders.map(o => {
    const rows = (o.items || []).map(it => `
      <tr>
        <td style="padding:1px 0">${esc(it.quantity)}× ${esc(it.name || '')}</td>
        <td style="text-align:right;padding:1px 0;font-variant-numeric:tabular-nums">${fmtMoney(it.total ?? (it.price * it.quantity))}</td>
      </tr>
    `).join('');
    return `
      <tr><td colspan="2" style="padding-top:10px;font-weight:600;font-size:11px;color:#666">Order #${esc(String(o.id).slice(0, 8))} · ${esc(tabLabel(o.table_number))}</td></tr>
      ${rows}
    `;
  }).join('');

  const partialRows = (partials || []).map(p => `
    <tr>
      <td style="padding:1px 0;text-transform:uppercase;font-size:10px;letter-spacing:1px">${esc(p.method)}</td>
      <td style="text-align:right;padding:1px 0;font-variant-numeric:tabular-nums">${fmtMoney(p.amount)}</td>
    </tr>
  `).join('');

  return `<!doctype html>
<html><head><meta charset="utf-8"><title>Majnu · Tab Receipt</title>
<style>
  body{font-family:'Courier New',monospace;color:#111;max-width:320px;margin:24px auto;padding:0 16px;line-height:1.45}
  h1{font-family:Georgia,serif;text-align:center;font-size:18px;margin:0 0 4px}
  .meta{text-align:center;font-size:11px;color:#555;margin-bottom:14px}
  table{width:100%;border-collapse:collapse;font-size:12px}
  .sep{border-top:1px dashed #999;margin:10px 0}
  .total{font-size:14px;font-weight:700}
  .foot{text-align:center;font-size:10px;color:#777;margin-top:18px;letter-spacing:1px}
</style></head><body>
  <h1>Majnu Lounge &amp; Bar</h1>
  <div class="meta">
    Tab · ${esc(tab.customer_name || 'Guest')}<br>
    ${tab.is_guest ? 'Walk-in' : 'Member'} · Settled ${esc(settledAt)}
  </div>
  <table>${itemRows}</table>
  <div class="sep"></div>
  <table>
    <tr><td>Subtotal</td><td style="text-align:right">${fmtMoney(sumTotal)}</td></tr>
    ${discount > 0 ? `<tr><td>${esc(tab.discount_label || 'Discount')}</td><td style="text-align:right">−${fmtMoney(discount)}</td></tr>` : ''}
    ${upfront > 0 ? `<tr><td>Less upfront (${esc(tab.deposit_method || 'cash')})</td><td style="text-align:right">−${fmtMoney(upfront)}</td></tr>` : ''}
    <tr><td class="total">Owed at bar</td><td class="total" style="text-align:right">${fmtMoney(owed)}</td></tr>
  </table>
  ${partialRows ? `<div class="sep"></div>
    <table>
      <tr><td colspan="2" style="font-size:11px;font-weight:600;color:#666">Payment split</td></tr>
      ${partialRows}
    </table>` : ''}
  <div class="sep"></div>
  <table>
    <tr><td>Payment method</td><td style="text-align:right;text-transform:uppercase">${methodLabel}</td></tr>
  </table>
  <div class="foot">THANK YOU FOR VISITING</div>
  <script>window.onload=()=>{window.focus();window.print();}<\/script>
</body></html>`;
}

export default function TabReceiptButton({ tabId }) {
  const [loading, setLoading] = useState(false);
  if (!tabId) return null;

  const open = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/tabs/${tabId}/receipt`);
      if (!data?.orders?.length) {
        toast.error('No orders on this tab');
        return;
      }
      // Build the print HTML and ship it via a Blob URL — avoids
      // `document.write()` (universally flagged by static analysers)
      // and gives us a same-origin print window that can still call
      // window.print() from its own onload handler. All user-supplied
      // strings inside `renderReceiptHtml` are escaped via `esc()`.
      const blob = new Blob([renderReceiptHtml(data)], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank', 'width=420,height=720,noopener,noreferrer');
      if (!w) {
        URL.revokeObjectURL(url);
        toast.error('Allow pop-ups to print the tab receipt');
        return;
      }
      // Best-effort cleanup once the print window has loaded the Blob.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load tab receipt');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      size="sm"
      onClick={open}
      disabled={loading}
      className="h-7 bg-blue-accent/10 text-blue-accent border border-blue-accent/30 hover:bg-blue-accent/20 rounded-sm font-sans text-xs px-2 disabled:opacity-50"
      data-testid={`tab-receipt-${tabId}`}
    >
      <Receipt className="w-3 h-3 mr-1" />
      {loading ? 'Loading…' : 'Tab Receipt'}
    </Button>
  );
}
